import { useState } from "react";
import PropTypes from 'prop-types';
import { Row, List, Col, Progress, Tooltip, Modal, Table, Input, DatePicker, Select, Button } from 'antd';
import { ExclamationCircleTwoTone } from '@ant-design/icons';
import Box from '../../../components/utility/box';
import IntlMessages from '../../../components/utility/intlMessages';
import { DvIntlMessages } from "../../../components/utility/dvGeneralUtilities";
import moment from "moment";

const COLORS = {
  "INFO": "#6666ff",
  "ERROR": "#e50000",
  "DEBUG": "#329932",
  "WARNING": "#FFA500"
};
const DATE_FORMAT = 'DD/MM/YYYY';

function ClinicBackgroundLogs(props) {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState([]);

  function openModal(record) {
    setSelectedRecord(record)
    setModalVisible(visible => !visible);
  }

  function closeModal() {
    setSelectedRecord([])
    setModalVisible(visible => !visible);
  }

  function handleOnChangeDate(value) {
    if (value) props.setJobDate(moment(value).format(DATE_FORMAT));
    else props.setJobDate(null);
  }

  function handleOnChangeType(e) {
    const { value } = e.target;
    props.setJobType(value ? value : null)
  }

  function handleOnChangeLevel(value) {
    props.setJobLevel(value ? value : null)
  }

  const getLevel = (level) => {
    const color = COLORS[level];
    return <div style={{ color: color }}>{level}</div>
  }

  const getProgressStatus = (level, percentage) => {
    let status;

    const mapper = {
      "INFO": "active",
      "ERROR": "exception"
    }

    if (percentage === 100) {
      status = "success";
    } else {
      status = mapper[level];
    }
    return status;
  }

  const columns = [
    {
      key: "type",
      title: <IntlMessages id="homePage.backgrounLogs.type" />,
      render: (text, record) => {
        return record.logs[0].type
      }
    },
    {
      key: "id",
      title: <IntlMessages id="homePage.backgrounLogs.jobId" />,
      render: (text, record) => {
        return record.logs[0].id
      }
    },
    {
      key: "level",
      title: <IntlMessages id="databases.status" />,
      render: (text, record) => {
        return (
          <Row>
            {record.containsError &&
              <Col span={1}>
                <Tooltip title={<IntlMessages id="homePage.backgrounLogs.containsError" />}>
                  <ExclamationCircleTwoTone twoToneColor={'red'} />
                </Tooltip>
              </Col>
            }
            <Col>
              {getLevel(record.logs[0].level)}
            </Col>
          </Row>
        )
      }
    },
    {
      key: "status",
      title: <IntlMessages id="homePage.backgrounLogs.status" />,
      render: (text, record) => {
        return record.logs[0].status
      }
    },
    {
      key: "moreInfo",
      render: (text, record) => {
        const progressStatus = getProgressStatus(record.logs[0].level, record.logs[0].percentage);
        return (
          <Row gutter={[16, 16]}>
            <Col span={24} style={{ wordWrap: "break-word" }}>
              <IntlMessages id="homePage.backgrounLogs.userId" />: {record.logs[0].userId}
            </Col>
            <Col span={24} style={{ wordWrap: "break-word" }}>
              <IntlMessages id="homePage.backgrounLogs.datetime" />: {record.logs[0].datetime}
            </Col>
            <Col span={23} style={{ wordWrap: "break-word" }}>
              <Progress percent={record.logs[0].percentage} status={progressStatus} />
            </Col>
          </Row>
        );
      }
    },
  ];

  function backgroundJobLogListItemFactory(record) {
    const progressStatus = getProgressStatus(record.level, record.percentage);

    return (
      <List.Item>
        <Row style={{ width: "100%" }} gutter={[16, 16]}>
          <Col span={5} style={{ wordWrap: "break-word" }}>
            <IntlMessages id="homePage.backgrounLogs.type" />: {record.type}
          </Col>
          <Col span={2} style={{ wordWrap: "break-word" }}>
            <IntlMessages id="homePage.backgrounLogs.jobId" />: {record.id}
          </Col>
          <Col span={2} style={{ wordWrap: "break-word" }}>
            {getLevel(record.level)}
          </Col>
          <Col span={8} style={{ wordWrap: "break-word" }}>
            <IntlMessages id="homePage.backgrounLogs.status" />: {record.status}
          </Col>
          <Col span={7}>
            <Row gutter={[16, 16]}>
              <Col span={24} style={{ wordWrap: "break-word" }}>
                <IntlMessages id="homePage.backgrounLogs.userId" />: {record.userId}
              </Col>
              <Col span={24} style={{ wordWrap: "break-word" }}>
                <IntlMessages id="homePage.backgrounLogs.datetime" />: {record.datetime}
              </Col>
              <Col span={24} style={{ wordWrap: "break-word" }}>
                <Progress percent={record.percentage} status={progressStatus} />
              </Col>
            </Row>
          </Col>
        </Row>
      </List.Item>
    )
  }

  const tableColumns = columns.map(item => ({ ...item, ellipsis: true }));
  const paginationConfig = {
    showSizeChanger: true,
    defaultPageSize: props.recordsPerPage,
    pageSizeOptions: props.pageSizeOptions,
    current: props.currentPage,
    total: props.numberOfLogs
  };
  return (
    <Row gutter={[16, 16]} style={{ marginTop: "15px" }}>

      <Col span={24}>
        <Row gutter={16}>
          <Col>
            <Input
              placeholder={DvIntlMessages("homePage.backgrounLogs.type")}
              allowClear onChange={handleOnChangeType}
            />
          </Col>
          <Col>
            <Select style={{ width: 120 }} onChange={handleOnChangeLevel}
              placeholder={DvIntlMessages("databases.status")} allowClear={true}>
              {Object.keys(COLORS).map(value => {
                return (
                  <Select.Option value={value} key={value}>
                    {value}
                  </Select.Option>
                )
              })}
            </Select>
          </Col>
          <Col>
            <DatePicker
              format={DATE_FORMAT}
              placeholder={DvIntlMessages("homePage.backgrounLogs.datetime")}
              allowClear onChange={handleOnChangeDate} enterButton
            />
          </Col>
          <Col>
            <Button onClick={props.submitFilters} type='primary'>
              <IntlMessages id="general.submit" />
            </Button>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Box>
          <Row>
            <Col span={24}>
              <Table
                bordered={true}
                showHeader
                pagination={paginationConfig}
                columns={tableColumns}
                dataSource={props.data}
                onChange={props.updateTableData}
                rowKey={(record) => record.logs[0].id}
                numberOfLogs={props.numberOfLogs}
                onRow={(record, rowIndex) => {
                  return {
                    onClick: event => { openModal(record['logs']) },
                  };
                }}
              />

              <Modal visible={modalVisible} onCancel={closeModal} onOk={closeModal} width={1200} closable={false} footer={null}>
                <List
                  dataSource={selectedRecord}
                  bordered
                  renderItem={(record) => backgroundJobLogListItemFactory(record)}
                />
              </Modal>
            </Col>
          </Row>
        </Box>
      </Col>
    </Row>
  )
}

ClinicBackgroundLogs.propTypes = {
  data: PropTypes.array.isRequired,
  numberOfLogs: PropTypes.number.isRequired,

  pageSizeOptions: PropTypes.array.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  recordsPerPage: PropTypes.number.isRequired,
  setRecordsPerPage: PropTypes.func.isRequired,
  updateTableData: PropTypes.func.isRequired,
  setJobLevel: PropTypes.func.isRequired,
  setJobType: PropTypes.func.isRequired,
  setJobDate: PropTypes.func.isRequired,
  submitFilters: PropTypes.func.isRequired
};

ClinicBackgroundLogs.defaultProps = {
  data: []
}

export default ClinicBackgroundLogs;